import {
    FetchOrgDestinationsAndVersionsSuccessAction,
    FETCH_ORG_DESTINATIONS_AND_VERSIONS_SUCCESS,
} from '../actions/orgDestinationsAndVersions';
import {
    SummaryViewDataRowAPI,
    SummaryViewResponseAPI,
    VersionSummaryViewDataItemAPI,
    OrgDestinationsResponse,
    SummaryViewPageCacheEntry,
} from '../types/summaryView';
import { LogoutSuccessAction, LOGOUT_SUCCESS } from '../actions/login';
import { combineReducers } from 'redux';
import { ClearStateAction, CLEAR_STATE } from '../actions/state';
import { FetchOrgSummaryViewSuccessAction, FETCH_ORG_SUMMARY_VIEW_SUCCESS } from '../actions/orgSummaryView';

type SummaryViewsActionTypes =
    | LogoutSuccessAction
    | FetchOrgDestinationsAndVersionsSuccessAction
    | FetchOrgSummaryViewSuccessAction
    | ClearStateAction;

function buildVersionDestinationKey(item: VersionSummaryViewDataItemAPI) {
    return `${item.version}::${item.destinationId}`;
}

function mergeFetchedPropertiesByRowKey(
    existingByRowKey: Record<string, string[]> = {},
    incomingItems: VersionSummaryViewDataItemAPI[],
    fetchedProperties: string[],
) {
    const nextByRowKey = { ...existingByRowKey };

    incomingItems.forEach((item) => {
        const rowKey = buildVersionDestinationKey(item);
        nextByRowKey[rowKey] = Array.from(new Set([...(nextByRowKey[rowKey] ?? []), ...fetchedProperties]));
    });

    return nextByRowKey;
}

function mergeRows(existingRows: SummaryViewDataRowAPI[] = [], incomingRows: SummaryViewDataRowAPI[] = []) {
    const rowMap = new Map<string, SummaryViewDataRowAPI>();
    existingRows.forEach((row) => {
        rowMap.set(row.type, row);
    });
    incomingRows.forEach((row) => {
        rowMap.set(row.type, row);
    });
    return Array.from(rowMap.values());
}

function mergeSummaryItems(
    existingItems: VersionSummaryViewDataItemAPI[] = [],
    incomingItems: VersionSummaryViewDataItemAPI[] = [],
) {
    const itemMap = new Map<string, VersionSummaryViewDataItemAPI>();

    existingItems.forEach((item) => {
        itemMap.set(buildVersionDestinationKey(item), item);
    });

    incomingItems.forEach((incoming) => {
        const key = buildVersionDestinationKey(incoming);
        const existing = itemMap.get(key);

        if (existing) {
            itemMap.set(key, {
                ...existing,
                ...incoming,
                rows: mergeRows(existing.rows, incoming.rows),
            });
        } else {
            itemMap.set(key, incoming);
        }
    });

    return Array.from(itemMap.values());
}

export function mergeSummaryView(existing: SummaryViewResponseAPI | undefined, incoming: SummaryViewResponseAPI) {
    if (!existing) {
        return incoming;
    }

    return {
        ...existing,
        ...incoming,
        versionSummaryViewItems: mergeSummaryItems(existing.versionSummaryViewItems, incoming.versionSummaryViewItems),
    };
}

// Results are cached per query rather than per view: without this, clearing a keyword refetches the
// page you were looking at a second ago.
const MAX_CACHED_SUMMARY_VIEWS_PER_REQUEST = 10;

interface SummaryViewRequestCache {
    entriesByRowSetSignature: Record<string, SummaryViewPageCacheEntry>;
    recentRowSetSignatures: string[];
}

type SummaryViewsById = Record<string, SummaryViewRequestCache>;

function cacheSummaryViewEntry(
    cache: SummaryViewRequestCache | undefined,
    rowSetSignature: string,
    entry: SummaryViewPageCacheEntry,
): SummaryViewRequestCache {
    const recent = [rowSetSignature, ...(cache?.recentRowSetSignatures ?? []).filter((key) => key !== rowSetSignature)];
    const retained = recent.slice(0, MAX_CACHED_SUMMARY_VIEWS_PER_REQUEST);
    const retainedKeys = new Set(retained);

    const entries: Record<string, SummaryViewPageCacheEntry> = { [rowSetSignature]: entry };
    Object.entries(cache?.entriesByRowSetSignature ?? {}).forEach(([key, cachedEntry]) => {
        if (key !== rowSetSignature && retainedKeys.has(key)) {
            entries[key] = cachedEntry;
        }
    });

    return { entriesByRowSetSignature: entries, recentRowSetSignatures: retained };
}

function summaryViewsById(state: SummaryViewsById = {}, action: SummaryViewsActionTypes): SummaryViewsById {
    switch (action.type) {
        case FETCH_ORG_SUMMARY_VIEW_SUCCESS: {
            const { requestId, rowSetSignature, scopeKey, fetchedAt, summaryView, fetchedProperties } = action.payload;
            const cache = state[requestId];
            const existingEntry = cache?.entriesByRowSetSignature[rowSetSignature];

            // Same query, more columns: merge so progressive property loading keeps what it has.
            const nextEntry: SummaryViewPageCacheEntry =
                existingEntry ?
                    {
                        fetchedAt,
                        scopeKey,
                        rowSetSignature,
                        fetchedProperties: Array.from(
                            new Set([...existingEntry.fetchedProperties, ...fetchedProperties]),
                        ),
                        fetchedPropertiesByRowKey: mergeFetchedPropertiesByRowKey(
                            existingEntry.fetchedPropertiesByRowKey,
                            summaryView.versionSummaryViewItems,
                            fetchedProperties,
                        ),
                        summaryView: mergeSummaryView(existingEntry.summaryView, summaryView),
                    }
                :   {
                        fetchedAt,
                        scopeKey,
                        rowSetSignature,
                        fetchedProperties: [...fetchedProperties],
                        fetchedPropertiesByRowKey: mergeFetchedPropertiesByRowKey(
                            {},
                            summaryView.versionSummaryViewItems,
                            fetchedProperties,
                        ),
                        summaryView,
                    };

            return {
                ...state,
                [requestId]: cacheSummaryViewEntry(cache, rowSetSignature, nextEntry),
            };
        }
        case LOGOUT_SUCCESS:
        case CLEAR_STATE:
            return {};
        default:
            return state;
    }
}

type OrgDestinationsByOrgId = Record<string, OrgDestinationsResponse>;

function orgDestinationsAndVersions(
    state: OrgDestinationsByOrgId = {},
    action: SummaryViewsActionTypes,
): OrgDestinationsByOrgId {
    switch (action.type) {
        case FETCH_ORG_DESTINATIONS_AND_VERSIONS_SUCCESS:
            return {
                ...state,
                [action.payload.orgId]: action.payload.data,
            };
        case LOGOUT_SUCCESS:
        case CLEAR_STATE:
            return {};
        default:
            return state;
    }
}

const summaryViewsReducer = combineReducers({
    byId: summaryViewsById,
    orgDestinations: orgDestinationsAndVersions,
});

export default summaryViewsReducer;
